import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { ApiError, type UserIdRow } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";
import { CopyableId, CopyableEmail } from "../components/CopyableId";
import { TypeEmailConfirm } from "../components/UserDetailPanel";

type Report = {
  bullclipOnly: UserIdRow[]; // Drawboard Projects profile, no identity — can't sign in
  identityOnly: UserIdRow[]; // identity, no Drawboard Projects profile — no app profile/org
  projectsTotal: number;
  identityTotal: number;
  systemUsersExcluded: number;
};

// Purpose-made users that intentionally exist on only one side of the reconciliation — they'd
// show up as false positives here:
//  - @apikeys.drawboard.com — backing users for API keys (Projects profile, no identity)
//  - @example.com           — demo-project sample users (Projects profile, no identity)
//  - <userId>@drawboard.com — deleted users: the profile is kept (anonymized) but the identity is
//    deleted, so they'd otherwise all report as broken. Exact-match only — staff have real
//    @drawboard.com addresses.
const EXCLUDED_EMAIL_SUFFIXES = ["@apikeys.drawboard.com", "@example.com"];
const isSystemUser = (u: UserIdRow) => {
  const email = (u.email ?? "").toLowerCase();
  return EXCLUDED_EMAIL_SUFFIXES.some((suffix) => email.endsWith(suffix))
    || email === `${u.userId.toLowerCase()}@drawboard.com`;
};

// The domain the bulk-delete button clears. Broken qq.com accounts are spam sign-ups: numeric QQ
// addresses with no usable account on one side, so nobody can be relying on them.
const BULK_DELETE_DOMAIN = "qq.com";
const isBulkDeleteTarget = (u: UserIdRow) =>
  (u.email ?? "").toLowerCase().endsWith(`@${BULK_DELETE_DOMAIN}`);

// The no-identity list runs to tens of thousands of rows in production; rendering them all locks the
// tab. Past this many the table says so and asks for a filter instead.
const MAX_RENDERED_ROWS = 500;
// Failures from a bulk run share a handful of causes; past this many the rest are counted, not listed.
const MAX_RENDERED_FAILURES = 50;

// Deletes run one at a time. The endpoint's last-administrator guard counts the other administrators
// and then dispatches the delete, with nothing serialising the two per organization, so two
// concurrent deletes of an organization's last two administrators could each count the other as
// still there and both pass. One at a time is also gentler on the silo, since each delete is a full
// DeleteUserRequest (access scrub, anonymize, login delete).
const BULK_DELETE_CONCURRENCY = 1;
// A 429 from this endpoint carries no advised wait, so retries fall back to this.
const RATE_LIMIT_FALLBACK_SECONDS = 5;

const STOPPED = Symbol("stopped");

type BulkFailure = { user: UserIdRow; error: string };
type BulkRun = {
  total: number;
  done: number;
  failures: BulkFailure[];
  running: boolean;
  stopped: boolean;
};

const matchesFilter = (u: UserIdRow, needle: string) =>
  !needle
  || u.userId.toLowerCase().includes(needle)
  || (u.email ?? "").toLowerCase().includes(needle);

/**
 * Reconciles the two systems that share a user id — Drawboard Projects (Bullclip backend) and the
 * identity server. A user present in only one is broken. The id lists are pulled from each backend
 * and diffed client-side.
 */
export function BrokenUsersPage() {
  const api = useApi();
  const navigate = useNavigate();
  useSetTopbarTabs([
    { id: "users", label: "Users", active: false, onSelect: () => navigate("/support/users") },
    { id: "guests", label: "Guest users", active: false, onSelect: () => navigate("/support/guest-users") },
    { id: "broken", label: "Broken users", active: true },
  ]);

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const [bulk, setBulk] = useState<BulkRun | null>(null);
  const stopRequested = useRef(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      const [projects, identity] = await Promise.all([
        api.getBullclipUserIds(),
        api.getIdentityUserIds(),
      ]);
      const identityIds = new Set(identity.map((u) => u.userId));
      const projectIds = new Set(projects.map((u) => u.userId));
      const bullclipOrphans = projects.filter((u) => !identityIds.has(u.userId));
      const identityOrphans = identity.filter((u) => !projectIds.has(u.userId));
      const bullclipOnly = bullclipOrphans.filter((u) => !isSystemUser(u));
      const identityOnly = identityOrphans.filter((u) => !isSystemUser(u));
      setReport({
        bullclipOnly,
        identityOnly,
        projectsTotal: projects.length,
        identityTotal: identity.length,
        systemUsersExcluded:
          bullclipOrphans.length - bullclipOnly.length + (identityOrphans.length - identityOnly.length),
      });
    } catch (e) {
      setError(`${e}`);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { void run(); }, [run]);

  // Leaving the page mid-run must stop the batch: once unmounted there is no Stop button and no
  // failure list, so the remaining deletes would carry on unseen.
  useEffect(() => () => { stopRequested.current = true; }, []);

  function openUser(u: UserIdRow) {
    const p = new URLSearchParams();
    p.set("user", u.userId);
    if (u.email) p.set("email", u.email);
    navigate(`/support/users?${p.toString()}`);
  }

  const needle = filter.trim().toLowerCase();
  const bullclipShown = useMemo(
    () => report?.bullclipOnly.filter((u) => matchesFilter(u, needle)) ?? [],
    [report, needle]);
  const identityShown = useMemo(
    () => report?.identityOnly.filter((u) => matchesFilter(u, needle)) ?? [],
    [report, needle]);

  // Both lists, de-duplicated by id: the delete endpoint handles either side being absent.
  const bulkTargets = useMemo(() => {
    if (!report) return [];
    const byId = new Map<string, UserIdRow>();
    for (const u of [...report.bullclipOnly, ...report.identityOnly]) {
      if (isBulkDeleteTarget(u)) byId.set(u.userId, u);
    }
    return [...byId.values()];
  }, [report]);

  // Returns null when deleted, an error message when refused, or STOPPED when Stop was pressed while
  // the row was waiting out a rate limit (it was never deleted, so it is neither done nor failed).
  async function deleteOne(u: UserIdRow): Promise<string | null | typeof STOPPED> {
    // Retry a 429 a few times before calling the row failed, unless Stop has been pressed meanwhile.
    for (let attempt = 0; ; attempt++) {
      try {
        await api.deleteUser(u.userId);
        return null;
      } catch (e) {
        if (e instanceof ApiError && e.status === 429 && attempt < 5) {
          if (stopRequested.current) return STOPPED;
          const waitSeconds = e.retryAfterSeconds ?? RATE_LIMIT_FALLBACK_SECONDS;
          await new Promise((resolve) => setTimeout(resolve, waitSeconds * 1000));
          if (stopRequested.current) return STOPPED;
          continue;
        }
        return e instanceof ApiError ? `${e.status}: ${e.message}` : `${e}`;
      }
    }
  }

  async function runBulkDelete() {
    const targets = bulkTargets;
    setConfirmingBulk(false);
    stopRequested.current = false;
    setBulk({ total: targets.length, done: 0, failures: [], running: true, stopped: false });

    let next = 0;
    const worker = async () => {
      while (!stopRequested.current && next < targets.length) {
        const u = targets[next++];
        const failure = await deleteOne(u);
        if (failure === STOPPED) break;
        setBulk((b) => b && {
          ...b,
          done: b.done + 1,
          failures: failure ? [...b.failures, { user: u, error: failure }] : b.failures,
        });
      }
    };
    await Promise.all(Array.from({ length: BULK_DELETE_CONCURRENCY }, worker));

    // Only "stopped" when rows were actually skipped: Stop pressed after the last row was taken
    // still finished the whole batch.
    setBulk((b) => b && { ...b, running: false, stopped: stopRequested.current && b.done < b.total });
    // The lists are stale now; rebuild them from the servers rather than patching them locally.
    await run();
  }

  const clean = report != null && report.bullclipOnly.length === 0 && report.identityOnly.length === 0;
  const busy = bulk?.running === true;

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Support · Users · Broken users</div>
        <h1>Broken users</h1>
        <p>
          Reconciles Drawboard Projects and the identity server, which share a user id. A user present
          in only one system is broken — no identity record means they can't sign in; no Drawboard
          Projects profile means they have no app profile or organization. Purpose-made system users
          are excluded: <span className="mono">@apikeys.drawboard.com</span> (API keys) and{" "}
          <span className="mono">@example.com</span> (demo-project sample users).
        </p>
      </div>

      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> Reconciliation
          <span className="hint">
            {report
              ? `${report.projectsTotal} Projects · ${report.identityTotal} identity users` +
                (report.systemUsersExcluded > 0 ? ` · ${report.systemUsersExcluded} system users excluded` : "")
              : "compares all user ids in both systems"}
          </span>
          <button
            className={"btn btn-ghost btn-sm" + (loading ? " loading" : "")}
            style={{ marginLeft: "auto" }}
            disabled={loading || busy}
            onClick={() => void run()}
          >
            Re-run
          </button>
        </header>
        <div className="body">
          {error && (
            <div className="callout error"><span className="mk">err</span><span>{error}</span></div>
          )}
          {loading && !report && <div className="hint">reconciling…</div>}
          {clean && (
            <div className="callout ok">
              <span className="mk">ok</span>
              <span>No mismatches found — every user exists in both systems.</span>
            </div>
          )}

          {bulk && (
            <BulkProgress
              bulk={bulk}
              onStop={() => { stopRequested.current = true; }}
              onDismiss={() => setBulk(null)}
            />
          )}

          {report && !clean && (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 18 }}>
                <input
                  type="search"
                  placeholder="Filter by email or user id…"
                  spellCheck={false}
                  autoComplete="off"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{ flex: "1 1 240px", minWidth: 0 }}
                />
                {bulkTargets.length > 0 && !confirmingBulk && (
                  <button
                    className="btn btn-danger btn-sm"
                    style={{ whiteSpace: "nowrap" }}
                    disabled={busy}
                    onClick={() => setConfirmingBulk(true)}
                  >
                    Delete {bulkTargets.length} {BULK_DELETE_DOMAIN} users…
                  </button>
                )}
              </div>

              {confirmingBulk && (
                <div style={{ marginBottom: 18 }}>
                  <TypeEmailConfirm
                    confirmTarget={BULK_DELETE_DOMAIN}
                    confirmLabel={`Permanently delete ${bulkTargets.length} users`}
                    busy={false}
                    onConfirm={() => void runBulkDelete()}
                    onCancel={() => setConfirmingBulk(false)}
                    description={
                      <>
                        <b>Permanently delete</b> every broken user with an{" "}
                        <b className="mono">@{BULK_DELETE_DOMAIN}</b> address —{" "}
                        <b>{bulkTargets.length}</b> across both lists, whatever the filter shows, one at a
                        time. Each one goes through the same delete as the user panel: access is revoked
                        and whichever of the profile and the identity login exists is removed (the profile
                        is anonymized). An audit entry is recorded only for users who belonged to an
                        organization. An administrator of an organization that still has other members is
                        refused and listed as a failure. It cannot be undone.
                      </>
                    }
                  />
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
                {report.bullclipOnly.length > 0 && (
                  <OrphanTable
                    title={`No identity record · ${countLabel(bullclipShown.length, report.bullclipOnly.length, needle)}`}
                    subtitle="Has a Drawboard Projects profile, but no identity-server account — cannot sign in."
                    rows={bullclipShown}
                    onOpen={openUser}
                  />
                )}
                {report.identityOnly.length > 0 && (
                  <OrphanTable
                    title={`No Drawboard Projects profile · ${countLabel(identityShown.length, report.identityOnly.length, needle)}`}
                    subtitle="Has an identity-server account, but no Drawboard Projects profile / organization."
                    rows={identityShown}
                    onOpen={openUser}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

const countLabel = (shown: number, total: number, needle: string) =>
  needle ? `${shown} of ${total}` : `${total}`;

function BulkProgress({ bulk, onStop, onDismiss }: {
  bulk: BulkRun;
  onStop: () => void;
  onDismiss: () => void;
}) {
  const failed = bulk.failures.length;
  const deleted = bulk.done - failed;
  const kind = bulk.running ? "warn" : failed > 0 ? "error" : "ok";
  const status = bulk.running
    ? `Deleting… ${bulk.done} of ${bulk.total} done`
    : bulk.stopped
      ? `Stopped after ${bulk.done} of ${bulk.total}`
      : `Finished ${bulk.total} of ${bulk.total}`;

  return (
    <div className={`callout ${kind}`} style={{ marginBottom: 18 }}>
      <span className="mk">{bulk.running ? "run" : failed > 0 ? "err" : "ok"}</span>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span>
            {status} — {deleted} deleted{failed > 0 ? `, ${failed} failed` : ""}.
          </span>
          <span style={{ flex: 1 }} />
          {bulk.running ? (
            <button className="btn btn-ghost btn-sm" onClick={onStop}>Stop</button>
          ) : (
            <button className="btn btn-ghost btn-sm" onClick={onDismiss}>Dismiss</button>
          )}
        </div>
        {failed > 0 && (
          <div style={{ overflowX: "auto", marginTop: 10 }}>
          <table className="grid">
            <thead>
              <tr>
                <th>Email</th>
                <th>User id</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {bulk.failures.slice(0, MAX_RENDERED_FAILURES).map((f) => (
                <tr key={f.user.userId}>
                  <td><CopyableEmail email={f.user.email} /></td>
                  <td><CopyableId value={f.user.userId} /></td>
                  <td style={{ overflowWrap: "anywhere" }}>{f.error}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {failed > MAX_RENDERED_FAILURES && (
          <div className="hint" style={{ marginTop: 8 }}>
            Showing the first {MAX_RENDERED_FAILURES} of {failed} failures. The failed users stay in the
            lists below after the re-run, so filter there to find them.
          </div>
        )}
      </div>
    </div>
  );
}

function OrphanTable({ title, subtitle, rows, onOpen }: {
  title: string;
  subtitle: string;
  rows: UserIdRow[];
  onOpen: (u: UserIdRow) => void;
}) {
  const rendered = rows.length > MAX_RENDERED_ROWS ? rows.slice(0, MAX_RENDERED_ROWS) : rows;
  return (
    <div>
      <div className="banner-broken" style={{ marginBottom: 0 }}>
        <div className="banner-broken-title">{title}</div>
        <div className="banner-broken-body">{subtitle}</div>
      </div>
      <div style={{ border: "1px solid var(--red)", borderTop: "none", overflowX: "auto" }}>
        {rows.length === 0 ? (
          <div className="hint" style={{ padding: 16 }}>No users in this list match the filter.</div>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Email</th>
                <th>User id</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rendered.map((u) => (
                <tr key={u.userId} style={{ cursor: "pointer" }} onClick={() => onOpen(u)}>
                  <td><CopyableEmail email={u.email} /></td>
                  <td><CopyableId value={u.userId} /></td>
                  <td className="actions">
                    <button className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); onOpen(u); }}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {rows.length > MAX_RENDERED_ROWS && (
          <div className="hint" style={{ padding: 12 }}>
            Showing the first {MAX_RENDERED_ROWS} of {rows.length} — filter by email or user id to narrow the list.
          </div>
        )}
      </div>
    </div>
  );
}
